import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, catchError, map, of, tap, throwError } from "rxjs";
import { GetRefereeFormResult, GetRefereeStatusResult, RefereeBasicForm, WebinarData } from "../pages/referee/referee.models";
import { GetRefereesResult, GetReferenceApplicantRequestStatusResult, Referee } from "../pages/referencing/referencing.models";
import { ConfigService } from "./config.service";
import { ApplicantsSiteNavOnboarding, GetSiteConfigResult } from "../models/site.config.models";
import { LocalStorage } from "../constants/local-storage";
import { CandidateMeetingModel, CronofySlot, DeclineInterviewRequest, GetAllMeetingsForCandidateResponse, GetCronofyElementTokenResponse, InterviewDashboardResult, InterviewerModel } from "../pages/interviews/interviews.models";
import { GroupSlots } from "../pages/interviews/pages/interview-details/group-slots.interface";


@Injectable()
export class HttpService {
  private applicantSitesNav: BehaviorSubject<ApplicantsSiteNavOnboarding | null> = new BehaviorSubject<ApplicantsSiteNavOnboarding | null>(null);
  public applicantSitesNav$: Observable<ApplicantsSiteNavOnboarding | null> = this.applicantSitesNav.asObservable();
  private accessToken: string|null = '';
  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService
  ) { }

  getHeaders(): HttpHeaders {
    if (!this.accessToken){
      this.accessToken = localStorage.getItem('accessToken');
      // if this doesn't exist, redirect to an access denied page
      // TODO: interceptor to do that automatically if access token is denied
    }

    return new HttpHeaders()
    .set('content-type', 'application/json')
    .set('Access-Control-Allow-Origin', '*')
    .set('AccessToken', this.accessToken!)
    .set('ObfuscatedSiteId', localStorage.getItem(LocalStorage.OBFUSCATED_SITE_ID)!);
  }

  getSiteConfig(): Observable<GetSiteConfigResult> {
    let host = document.location.hostname;
    return this.httpClient.get<GetSiteConfigResult>(`${this.configService.careersSitesApi}/api/onboardingsites/site/config/get?host=${host}`).pipe(
      catchError((error) => {
        return throwError(() => new Error(error));
      })
    );
  }

  getReferees(): Observable<GetRefereesResult>{
    const url = `${this.configService.referencingApiUrl}/api/portal/referees/get`;

    return this.httpClient.get<GetRefereesResult>(url, { 'headers': this.getHeaders() });
  }

  getReferenceApplicantRequestStatusAndBasicInfo(): Observable<GetReferenceApplicantRequestStatusResult> {
    const url = `${this.configService.referencingApiUrl}/api/portal/referenceapplicantrequeststatus/get`;

    return this.httpClient.get<GetReferenceApplicantRequestStatusResult>(url, { 'headers': this.getHeaders() });
  }

  saveRefereeDetails(referee: Referee): Observable<any>{
    const url = `${this.configService.referencingApiUrl}/api/portal/referee/save`;

    return this.httpClient.post<any>(url, referee, { 'headers': this.getHeaders() });
  }

  sendRefereeEmail(refereeId: number): Observable<any>{
    const url = `${this.configService.referencingApiUrl}/api/portal/refereeemail/send?refereeId=${refereeId}`;

    return this.httpClient.post<any>(url, null, { 'headers': this.getHeaders() });
  }

  getRefereeForm(): Observable<GetRefereeFormResult> {
    const url = `${this.configService.referencingApiUrl}/api/refereeform/get`;

    return this.httpClient.get<GetRefereeFormResult>(url, { 'headers': this.getHeaders() });
  }

  saveRefereeForm(formInfo: any): Observable<any>{
    const url = `${this.configService.referencingApiUrl}/api/refereeform/save`;

    return this.httpClient.post<any>(url, formInfo, { 'headers': this.getHeaders() });
  }

  getRefereeStatus(): Observable<GetRefereeStatusResult>{
    const url = `${this.configService.referencingApiUrl}/api/portal/refereestatus/get`;

    return this.httpClient.get<GetRefereeStatusResult>(url, { 'headers': this.getHeaders() });
  }

  saveBasicProfessionalForm(form: RefereeBasicForm): Observable<any>{
    const url = `${this.configService.referencingApiUrl}/api/portal/basicprofessionalform/save`;

    return this.httpClient.post<any>(url, form, { 'headers': this.getHeaders() });
  }

  getWebinarsData(): Observable<WebinarData>{
    const url = `${this.configService.referencingApiUrl}/api/portal/webinardata/get`;

    return this.httpClient.get<WebinarData>(url, { 'headers': this.getHeaders() });
  }

  getRoleDetails() {
    const url = `${this.configService.onboardingApiUrl}/api/onboarding/applicant/roledetails/get`;
    return this.httpClient.get<any>(url, { 'headers': this.getHeaders() }).pipe(
      catchError((error) => {
        return throwError(() => new Error(error));
      })
    );
  }

  getSiteNavDetails() : Observable<ApplicantsSiteNavOnboarding> {
    const url = `${this.configService.onboardingApiUrl}/api/onboarding/applicant/sitenavigation/get`;
    return this.httpClient.get<any>(url, { 'headers': this.getHeaders() }).pipe(
      map(result => result.applicantSiteNavigation),
      tap(result => this.applicantSitesNav.next(result)),
      catchError((error) => {
        return throwError(() => new Error(error));
      })
    );
  }

  getInterviewDashboardInfo(): Observable<InterviewDashboardResult> {
    const url = `${this.configService.interviewSchedulerApiUrl}/api/portal/dashboard/get`;

    return this.httpClient.get<InterviewDashboardResult>(url, { 'headers': this.getHeaders() });
  }

  getAllMeetingsForCandidate(): Observable<GetAllMeetingsForCandidateResponse> {
    const url = `${this.configService.interviewSchedulerApiUrl}/api/portal/meetings/list`;

    return this.httpClient.get<GetAllMeetingsForCandidateResponse>(url, { 'headers': this.getHeaders() });
  }

  getInterviewersForMeeting(meetingId: number): Observable<InterviewerModel[]> {
    const url = `${this.configService.interviewSchedulerApiUrl}/api/portal/interviewers/get?meetingId=${meetingId}`;

    return this.httpClient.get<InterviewerModel[]>(url, { 'headers': this.getHeaders() });
  }

  confirmInterview(meetingId: number): Observable<any> {
    const url = `${this.configService.interviewSchedulerApiUrl}/api/portal/interview/confirm`;
    const data = { meetingId };
    return this.httpClient.post<any>(url, data, { 'headers': this.getHeaders() });
  }

  confirmSelfSelectedInterview(meetingId: number, slot: CronofySlot): Observable<CandidateMeetingModel> {
    const url = `${this.configService.interviewSchedulerApiUrl}/api/portal/interview/confirmslot`;

    const data = {
      meetingId,
      start: slot.start,
      end: slot.end
    }

    return this.httpClient.post<CandidateMeetingModel>(url, data, { 'headers': this.getHeaders() });
  }

  declineInterview(request: DeclineInterviewRequest): Observable<any> {
    const url = `${this.configService.interviewSchedulerApiUrl}/api/portal/interview/decline`;
    const data = { meetingId: request.meeting.id, notes: request.declineNotes };
    return this.httpClient.post<any>(url, data, { 'headers': this.getHeaders() });
  }

  authenticateCronofyElementToken(meetingId: number): Observable<GetCronofyElementTokenResponse> {
    let host = location.protocol + '//' + location.host;
    const url = `${this.configService.interviewSchedulerApiUrl}/api/portal/cronofy/authenticate/elementtoken?meetingId=${meetingId}&host=${host}`;
    return this.httpClient.get<GetCronofyElementTokenResponse>(url, { 'headers': this.getHeaders() });
  }

  getAllGroupMeetingsForCandidate(id: number): Observable<GroupSlots> {
    const url = `${this.configService.interviewSchedulerApiUrl}/api/portal/group-meeting/get/` + id;
    return this.httpClient.get<GroupSlots>(url, { 'headers': this.getHeaders() });
  }

  confirmGroupInterview(meetingId: number): Observable<any> {
    const url = `${this.configService.interviewSchedulerApiUrl}/api/portal/group-meeting/confirm`;
    const data = { meetingId };
    return this.httpClient.post<any>(url, data, { 'headers': this.getHeaders() });
  }

  declineGroupInterview(request: DeclineInterviewRequest, afterAccepting = false): Observable<any> {
    const url = `${this.configService.interviewSchedulerApiUrl}/api/portal/group-meeting/decline`;
    const data = afterAccepting
      ? { meetingId: request.meeting.id, notes: request.declineNotes, groupId: null }
      : { meetingId: null, notes: request.declineNotes, groupId: request.meeting.id };
    return this.httpClient.post<any>(url, data, { headers: this.getHeaders() });
  }
}
